import message from "@/utils/globalMessage";
import React, { useState, useEffect } from 'react';
import { Modal, Spin } from "antd";
import { useNavigate } from 'react-router-dom';
import { useMount, useRequest } from 'ahooks';
import { api } from '@/redux/api';
import { useFacilityId } from '@/hooks/use-facilityid';
import { useKeepAliveContext } from '@/hooks/use-keep-alive-context';
import { emitEvent, EventName, useEventBus } from '@/hooks/useEventBus';
import { useSelector } from 'react-redux';
import Auth from '@/components/Auth';
import moment from 'moment';
import UpdateEUI from '../UpdateEUI';
import { buildFacilityPath, getFacilityContext } from '@/utils/route-context';
import {
  PageWrapper,
  Container,
  Header,
  ProgressBarWrapper,
  ProgressLine,
  ProgressStep,
  Content,
  StatusCard,
  Button,
  InfoCard,
  InfoRow,
  CertItems,
  CertItem,
  CertItemLeft,
  CertStatus,
  StatusBadge,
  ComparisonTable,
  SuccessAnimation,
  Spinner,
  DebugPanel,
  DrawerOverlay,
  DrawerContainer,
  DrawerHeader,
  DrawerContent,
  FloatingButton
} from './index.style';

// ==================== Main Component ====================
const CertifyPage = () => {
  const STATES = {
    INITIAL: 'INITIAL',
    PENDING_ASSIGNMENT: 'PENDING_ASSIGNMENT',
    ASSIGNED: 'ASSIGNED',
    IN_PROGRESS: 'IN_PROGRESS',
    SUCCESS: 'SUCCESS'
  };

  const STATE_TO_STEP = {
    'INITIAL': 1,
    'PENDING_ASSIGNMENT': 2,
    'ASSIGNED': 3,
    'IN_PROGRESS': 4,
    'SUCCESS': 5
  };

  const navigate = useNavigate();
  const facility_id = useFacilityId();
  const { refreshTab } = useKeepAliveContext();
  const currentUserId = useSelector(state => state.Auth.userId);
  const facilityContext = useSelector((state) => getFacilityContext(state));
  const [currentState, setCurrentState] = useState(STATES.INITIAL);
  const [taskData, setTaskData] = useState(null);
  const [drawerVisible, setDrawerVisible] = useState(false);

  // Get certification status data (Gas/Elec) - 仍然需要用于显示进度
  const { data: certificationData = {
    electricity: { total: 0, certified: 0, percentage: 0 },
    gas: { total: 0, certified: 0, percentage: 0 },
    overall: { total: 0, certified: 0, percentage: 0 }
  }, loading: certificationDataLoading, run: runCertificationData } = useRequest(() => api.getEUICertificationStatus(), {
    manual: true
  });

  // Get facility task information - 使用新接口
  const { data: facilityTaskData, loading: facilityTaskLoading, run: runFacilityTask } = useRequest(
    () => api.getFacilityTask(facility_id), 
    {
      manual: true,
      onSuccess: (data) => {
        if (data && data.task) {
          setTaskData({
            ...data.task,
            last_completed_date: data.last_completed_date
          });
        } else {
          setTaskData(null);
        }
      }
    }
  );

  const isLoading = facilityTaskLoading || certificationDataLoading;

  // Initialize state based on data - 完全基于后端状态
  const initStep = (taskData, certificationData) => {
    // 没有任务 - 显示第一步
    if (!taskData) {
      setCurrentState(STATES.INITIAL);
      return;
    }

    const taskStatus = taskData.status;

    // 根据后端状态决定显示哪个步骤
    switch (taskStatus) {
      case 'Submitted':
        // 已提交，等待分配
        setCurrentState(STATES.PENDING_ASSIGNMENT);
        break;
      
      case 'Assigned':
        // 已分配，等待接受或拒绝
        setCurrentState(STATES.ASSIGNED);
        break;
      
      case 'Accepted':
        // 已接受，进行中（包含认证和更新 EUI）
        setCurrentState(STATES.IN_PROGRESS);
        break;
      
      case 'Rejected':
        // 已拒绝，等待重新分配
        setCurrentState(STATES.ASSIGNED); // 使用 ASSIGNED 状态显示，但会显示不同内容
        break;
      
      case 'Completed':
        // 已完成
        setCurrentState(STATES.SUCCESS);
        break;
      
      default:
        setCurrentState(STATES.INITIAL);
    }
  };

  useEffect(() => {
    initStep(taskData, certificationData);
  }, [taskData, certificationData]);

  const fetchData = () => {
    runCertificationData();
    runFacilityTask();
  };

  useMount(() => {
    fetchData();
  });

  useEventBus(EventName.UPDATE_CERTIFICATION_STATUS, () => {
    fetchData();
  });

  const changeStatus = (newState) => {
    if (!STATES[newState]) {
      console.error('Invalid state:', newState);
      return;
    }
    console.log(`State transition: ${currentState} -> ${newState}`);
    setCurrentState(newState);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Request certification
  const requestCertification = () => {
    api.requestEUITask(facility_id).then(() => {
      refreshTab();
      fetchData();
      emitEvent(EventName.REFRESH_CERTIFY_REQUEST);
      message.success('Certification request submitted successfully');
    }).catch(() => {
      message.error('Failed to submit certification request');
    });
  };

  // Update EUI
  const updateEUI = (euidata) => {
    if (!taskData) {
      message.error('No task found');
      return;
    }
    
    // UpdateEUI component already handles confirmation and data filtering
    api.updateBenchmarkEUI(facility_id, euidata, taskData.id).then(res => {
      if (res.status === 200) {
        fetchData();
        emitEvent(EventName.REFRESH_CERTIFY_REQUEST);
        message.success('EUI data updated successfully');
        closeDrawer(); // Close the drawer on success
      }
    }).catch(() => {
      message.error('Failed to update EUI data');
    });
  };

  // Accept task
  const acceptTask = () => {
    if (!taskData) {
      message.error('No task found');
      return;
    }
    api.acceptEUITask(taskData.id).then(() => {
      fetchData();
      emitEvent(EventName.REFRESH_CERTIFY_REQUEST);
      message.success('Task accepted successfully');
    }).catch(() => {
      message.error('Failed to accept task');
    });
  };

  // Reject task
  const rejectTask = () => {
    if (!taskData) {
      message.error('No task found');
      return;
    }
    Modal.confirm({
      title: 'Reject Task',
      content: 'Are you sure you want to reject this task?',
      okText: 'Confirm',
      cancelText: 'Cancel',
      onOk: () => {
        api.rejectEUITask(taskData.id).then(() => {
          fetchData();
          emitEvent(EventName.REFRESH_CERTIFY_REQUEST);
          message.success('Task rejected successfully');
        }).catch(() => {
          message.error('Failed to reject task');
        });
      }
    });
  };

  // Recertify confirmation
  const handleRecertify = () => {
    Modal.confirm({
      title: "Recertify?",
      content: `Facility data was last certified on ${moment(taskData?.last_completed_date).format('YYYY-MM-DD HH:mm:ss')}. Do you still want to reqeust for re-certification?`,
      okText: "Yes",
      cancelText: "No",
      onOk: requestCertification,
    });
  };

  // 打开/关闭抽屉
  const openDrawer = () => setDrawerVisible(true);
  const closeDrawer = () => setDrawerVisible(false);

  // Navigate to Gas or Elec page
  const goToPage = (pageType) => {
    let baseUrl = '';
    if (pageType === 'elec') {
      emitEvent(EventName.CLOSE_ELECTRICITY);
      baseUrl = buildFacilityPath('electricity', facilityContext) || '/switch-view';
    } else if (pageType === 'gas') {
      emitEvent(EventName.CLOSE_GAS);
      baseUrl = buildFacilityPath('gas', facilityContext) || '/switch-view';
    }
    navigate(baseUrl);
  };

  const getProgressWidth = () => {
    const currentStep = STATE_TO_STEP[currentState] || 1;
    return ((currentStep - 1) / 4) * 100;
  };

  const isStepActive = (step) => STATE_TO_STEP[currentState] === step;
  const isStepCompleted = (step) => STATE_TO_STEP[currentState] > step;

  const isElecCompleted = certificationData.electricity.certified === certificationData.electricity.total;
  const isGasCompleted = certificationData.gas.certified === certificationData.gas.total;
  const isCertified = isElecCompleted && isGasCompleted;

  return (
    <Spin spinning={isLoading} tip="Loading certification data..." size="large">
      <PageWrapper>
        <Container>
          {/* <Header>
            <h1>🔐 Certification Management</h1>
            <p className="subtitle">Data Certification Process Management System</p>
          </Header> */}

        {/* Progress Bar */}
        <ProgressBarWrapper>
          <ProgressLine width={getProgressWidth()} />
          <ProgressStep active={isStepActive(1)} completed={isStepCompleted(1)}>
            <div className="step-circle">1</div>
            <div className="step-label">Request</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(2)} completed={isStepCompleted(2)}>
            <div className="step-circle">2</div>
            <div className="step-label">Pending</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(3)} completed={isStepCompleted(3)}>
            <div className="step-circle">3</div>
            <div className="step-label">Assigned</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(4)} completed={isStepCompleted(4)}>
            <div className="step-circle">4</div>
            <div className="step-label">In Progress</div>
          </ProgressStep>
          <ProgressStep active={isStepActive(5)} completed={isStepCompleted(5)}>
            <div className="step-circle">5</div>
            <div className="step-label">Completed</div>
          </ProgressStep>
        </ProgressBarWrapper>

        <Content>
          {/* State 1: Initial */}
          <StatusCard active={currentState === STATES.INITIAL}>
            <div className="status-header">
              <div className="status-icon">📋</div>
              <div className="status-text">
                <h2 className="status-title">Ready to request data certification?</h2>
                <p className="status-description">
                  Click the button below to initiate the certification process for your utility data. The request will be submitted to an administrator who will assign a certification specialist.
                </p>
              </div>
            </div>
            <Auth code="certify_menu" level={2}>
              <Button variant="primary" onClick={requestCertification}>
                🚀 Request Certification
              </Button>
            </Auth>
          </StatusCard>

          {/* State 2: Pending Assignment */}
          <StatusCard active={currentState === STATES.PENDING_ASSIGNMENT}>
            <div className="status-header">
              <div className="status-icon">⏳</div>
              <div className="status-text">
                <h2 className="status-title">Waiting for task assignment</h2>
                <p className="status-description">
                  Certification request has been submitted successfully! The system is waiting for an administrator to assign a certification specialist. No further action is required at this time. You can check back later to view the latest status of the certification process.
                </p>
              </div>
            </div>
            <InfoCard>
              <InfoRow>
                <span className="info-time">{taskData?.request_at ? moment(taskData.request_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                <span className="info-label">Requested By</span>
                <span className="info-value">{taskData?.request_by_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-time"></span>
                <span className="info-label">Current Status</span>
                <span className="info-value">⏳ Pending Assignment</span>
              </InfoRow>
            </InfoCard>
          </StatusCard>

          {/* State 3: Assigned - 已分配 */}
          <StatusCard active={currentState === STATES.ASSIGNED}>
            {taskData?.status === 'Rejected' ? (
              // 如果任务被拒绝
              <>
                <div className="status-header">
                  <div className="status-icon">🔄</div>
                  <div className="status-text">
                    <h2 className="status-title">Task Rejected - Waiting for Reassignment</h2>
                    <p className="status-description">
                      This task has been rejected by the assigned user. Please wait for an administrator to re-assign.
                    </p>
                  </div>
                </div>
                <InfoCard>
                  <InfoRow>
                    <span className="info-time">{taskData?.request_at ? moment(taskData.request_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                    <span className="info-label">Requested By</span>
                    <span className="info-value">{taskData?.request_by_name || 'N/A'}</span>
                  </InfoRow>
                  <InfoRow>
                    <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                    <span className="info-label">Previously Assigned To</span>
                    <span className="info-value">{taskData?.assigned_to_name || 'N/A'}</span>
                  </InfoRow>
                  <InfoRow>
                    <span className="info-time"></span>
                    <span className="info-label">Current Status</span>
                    <span className="info-value" style={{ color: '#ff4d4f' }}>❌ Rejected</span>
                  </InfoRow>
                </InfoCard>
              </>
            ) : taskData?.assigned_to === currentUserId ? (
              // 如果当前用户是被分配者
              <>
                <div className="status-header">
                  <div className="status-icon">👤</div>
                  <div className="status-text">
                    <h2 className="status-title">Task assigned to you</h2>
                    <p className="status-description">
                      A certification task has been assigned to you. Please accept or reject this task assignment.
                    </p>
                  </div>
                </div>
                <InfoCard style={{ marginBottom: '20px' }}>
                  <InfoRow>
                    <span className="info-time">{taskData?.request_at ? moment(taskData.request_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                    <span className="info-label">Requested By</span>
                    <span className="info-value">{taskData?.request_by_name || 'N/A'}</span>
                  </InfoRow>
                  <InfoRow>
                    <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                    <span className="info-label">Assigned By</span>
                    <span className="info-value">{taskData?.assigned_by_name || 'N/A'}</span>
                  </InfoRow>
                  <InfoRow>
                    <span className="info-time"></span>
                    <span className="info-label">Current Status</span>
                    <span className="info-value">📋 Assigned to You</span>
                  </InfoRow>
                </InfoCard>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                  <Button variant="success" onClick={acceptTask}>
                    ✅ Accept Task
                  </Button>
                  <Button variant="secondary" onClick={rejectTask}>
                    ❌ Reject Task
                  </Button>
                </div>
              </>
            ) : (
              // 如果是其他用户
              <>
                <div className="status-header">
                  <div className="status-icon">⏳</div>
                  <div className="status-text">
                    <h2 className="status-title">Waiting for Assignee to Accept</h2>
                    <p className="status-description">
                      Task has been assigned to <span style={{ color: '#1890ff', fontWeight: 'bold' }}>{taskData?.assigned_to_name || 'a user'}</span>. Pengding Acceptance...
                    </p>
                  </div>
                </div>
                <InfoCard>
                  <InfoRow>
                    <span className="info-time">{taskData?.request_at ? moment(taskData.request_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                    <span className="info-label">Requested By</span>
                    <span className="info-value">{taskData?.request_by_name || 'N/A'}</span>
                  </InfoRow>
                  <InfoRow>
                    <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                    <span className="info-label">Assigned To</span>
                    <span className="info-value">{taskData?.assigned_to_name || 'N/A'}</span>
                  </InfoRow>
                  <InfoRow>
                    <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                    <span className="info-label">Assigned By</span>
                    <span className="info-value">{taskData?.assigned_by_name || 'N/A'}</span>
                  </InfoRow>
                  <InfoRow>
                    <span className="info-time"></span>
                    <span className="info-label">Current Status</span>
                    <span className="info-value">⏳ Waiting for Acceptance</span>
                  </InfoRow>
                </InfoCard>
              </>
            )}
          </StatusCard>

          {/* State 4: In Progress */}
          <StatusCard active={currentState === STATES.IN_PROGRESS}>
            <div className="status-header">
              <div className="status-icon">⚙️</div>
              <div className="status-text">
                <h2 className="status-title">
                  {certificationData.electricity.certified === certificationData.electricity.total && 
                   certificationData.gas.certified === certificationData.gas.total 
                    ? 'Certification Complete - Ready to Update EUI'
                    : 'Certify Pending Data'}
                </h2>
                <p className="status-description">
                  {certificationData.electricity.certified === certificationData.electricity.total && 
                   certificationData.gas.certified === certificationData.gas.total ? (
                    <>
                      All data has been certified successfully! 🎉 Click the floating button at the bottom right to review and update EUI data.
                    </>
                  ) : (
                    <>
                      There are <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '1.1em' }}>{certificationData.electricity.total - certificationData.electricity.certified}</span> Electricity and <span style={{ color: '#1890ff', fontWeight: 'bold', fontSize: '1.1em' }}>{certificationData.gas.total - certificationData.gas.certified}</span> Gas data entries pending certification. Please visit the respective pages to certify them.
                    </>
                  )}
                </p>
              </div>
            </div>
            <CertItems>
            <CertItem>
                <CertItemLeft>
                  <div className="cert-item-icon">⚡</div>
                  <div className="cert-item-info">
                    <h3>Electricity Data Certification</h3>
                  </div>
                </CertItemLeft>
                <CertStatus>
                  <StatusBadge completed={certificationData.electricity.certified === certificationData.electricity.total}>
                    {certificationData.electricity.certified === certificationData.electricity.total ? '✓' : '⏳'}
                    {certificationData.electricity.certified === certificationData.electricity.total ? ' Completed' : ' Pending'} ({certificationData.electricity.certified}/{certificationData.electricity.total})
                  </StatusBadge>
                  <Button variant="secondary" small onClick={() => goToPage('elec')}>Go to Page</Button>
                </CertStatus>
              </CertItem>
              <CertItem>
                <CertItemLeft>
                  <div className="cert-item-icon">🔥</div>
                  <div className="cert-item-info">
                    <h3>Gas Data Certification</h3>
                  </div>
                </CertItemLeft>
                <CertStatus>
                  <StatusBadge completed={certificationData.gas.certified === certificationData.gas.total}>
                    {certificationData.gas.certified === certificationData.gas.total ? '✓' : '⏳'} 
                    {certificationData.gas.certified === certificationData.gas.total ? ' Completed' : ' Pending'} ({certificationData.gas.certified}/{certificationData.gas.total})
                  </StatusBadge>
                  <Button variant="secondary" small onClick={() => goToPage('gas')}>Go to Page</Button>
                </CertStatus>
              </CertItem>


            </CertItems>

            {/* Preview Button */}
            {taskData && (
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '24px' }}>
                <Button 
                  variant="primary" 
                  onClick={openDrawer}
                  style={{ minWidth: '200px' }}
                >
                  📊 Preview & Update
                </Button>
              </div>
            )}
            {/* add a update Button */}
            <InfoCard style={{ marginBottom: '20px' }}>
              <InfoRow>
                <span className="info-time">{taskData?.request_at ? moment(taskData.request_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                <span className="info-label">Requested By</span>
                <span className="info-value">{taskData?.request_by_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                <span className="info-label">Assigned To</span>
                <span className="info-value">{taskData?.assigned_to_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                <span className="info-label">Assigned By</span>
                <span className="info-value">{taskData?.assigned_by_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-time"></span>
                <span className="info-label">Current Status</span>
                <span className="info-value">🔄 In Progress</span>
              </InfoRow>
            </InfoCard>


          </StatusCard>

          {/* State 5: Success */}
          <StatusCard active={currentState === STATES.SUCCESS}>
            <div className="status-header">
              <SuccessAnimation />
              <div className="status-text">
                <h2 className="status-title">🎉 Certification Complete!</h2>
                <p className="status-description">
                  Congratulations! All facility data has been certified and benchmark data is current.
                </p>
              </div>
            </div>

            <InfoCard>
              <InfoRow>
                <span className="info-time">{taskData?.request_at ? moment(taskData.request_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                <span className="info-label">Requested By</span>
                <span className="info-value">{taskData?.request_by_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                <span className="info-label">Assigned To</span>
                <span className="info-value">{taskData?.assigned_to_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-time">{taskData?.assign_at ? moment(taskData.assign_at).format('YYYY-MM-DD HH:mm') : ''}</span>
                <span className="info-label">Assigned By</span>
                <span className="info-value">{taskData?.assigned_by_name || 'N/A'}</span>
              </InfoRow>
              <InfoRow>
                <span className="info-time"></span>
                <span className="info-label">Final Status</span>
                <span className="info-value">✅ Completed</span>
              </InfoRow>
            </InfoCard>

            <div style={{ marginTop: '30px' }}>
              <Button variant="success" onClick={() => navigate(buildFacilityPath('inter-company-benchmark', facilityContext) || '/switch-view')}>
                📊 View Benchmark Page
              </Button>
              <Auth code="certify_menu" level={2}>
                <Button variant="primary" onClick={handleRecertify}>
                  🔄 Re-certify Request
                </Button>
              </Auth>
            </div>
          </StatusCard>
        </Content>

      </Container>

      {/* 浮动按钮 - 在有任务时显示 */}
      {taskData && (
        <FloatingButton onClick={openDrawer} title="View EUI Data">
          📊
        </FloatingButton>
      )}

      {/* 底部抽屉 - EUI 表格 */}
      <DrawerOverlay visible={drawerVisible} onClick={closeDrawer} />
      <DrawerContainer visible={drawerVisible}>
        <DrawerHeader>
          <h3>
            <span>📊</span>
            Benchmark Data
          </h3>
          <button className="close-btn" onClick={closeDrawer}>×</button>
        </DrawerHeader>
        <DrawerContent>
          {taskData ? (
            <UpdateEUI 
              updateEUI={updateEUI} 
              task={taskData} 
              isCertified={isCertified}
              visible={drawerVisible}
              currentState={currentState}
            />
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
              <p>No task data available</p>
            </div>
          )}
        </DrawerContent>
      </DrawerContainer>

      {/* Debug Control Panel */}
      {/* <DebugPanel>
        <h4>🎮 Debug Panel - State Controls</h4>
        <div>
          <button onClick={() => changeStatus(STATES.INITIAL)}>1. Initial</button>
          <button onClick={() => changeStatus(STATES.PENDING_ASSIGNMENT)}>2. Pending</button>
          <button onClick={() => changeStatus(STATES.ASSIGNED)}>3. Assigned</button>
        </div>
        <div style={{ marginTop: '5px' }}>
          <button onClick={() => changeStatus(STATES.IN_PROGRESS)}>4. In Progress</button>
          <button onClick={() => changeStatus(STATES.SUCCESS)}>5. Success</button>
        </div>
        <p>
          Current State: <span>{currentState}</span>
        </p>
        <p>
          Task Status: <span>{taskData?.status || 'None'}</span> | 
          Task ID: <span>{taskData?.id || 'N/A'}</span>
        </p>
        <p>
          Elec: {certificationData.electricity.certified}/{certificationData.electricity.total} | 
          Gas: {certificationData.gas.certified}/{certificationData.gas.total}
        </p>
      </DebugPanel> */}
    </PageWrapper>
    </Spin>
  );
};

export default CertifyPage;


